import { useAppSelector } from 'app/hooks';
import useConfidenceScore from 'hooks/use-confidence-score';
import { getTopMissingFields } from 'utils/confidence-score';

// Live recommendation-confidence chip (AHA-6796, memo §10): score % + band
// label + the top 3 missing fields by points ("which missing answer moves
// the score most"). Purely informational — never gates or blocks anything.
// With developer mode on (Debug Menu), expands to the full breakdown
// (DCS/PCS split + per-field table). Renders nothing off the ducted
// pathway or before a score resolves.

// Shared with the gate modal/banner (AHA-6796 enforcement UX) so every
// confidence surface colors bands identically.
export const BAND_STYLES: Record<string, string> = {
  high: 'bg-green-100 text-green-800',
  good: 'bg-emerald-50 text-emerald-700',
  moderate: 'bg-yellow-100 text-yellow-800',
  low: 'bg-red-100 text-red-700'
};

export default function ConfidenceScoreChip() {
  const score = useConfidenceScore();
  const developerMode = useAppSelector((state) => state.ui.developerMode);

  if (!score) {
    return null;
  }

  const bandStyle = BAND_STYLES[score.band.key] ?? 'bg-gray-100 text-gray-700';
  const topMissing = getTopMissingFields(score, 3);

  return (
    <div className='flex flex-col gap-2 rounded-lg border border-gray-200 bg-white p-4'>
      <div className='flex flex-wrap items-center gap-2'>
        <span className='text-sm font-semibold text-gray-900'>
          Recommendation Confidence
        </span>
        <span
          className={`inline-flex items-center gap-1 rounded-full px-2.5 py-0.5 text-sm font-semibold ${bandStyle}`}
        >
          {score.overall}%
          <span className='font-normal'>· {score.band.label}</span>
        </span>
        {score.hardGated && (
          <span className='rounded-full bg-red-50 px-2 py-0.5 text-xs font-medium text-red-700'>
            Key equipment detail missing
          </span>
        )}
      </div>
      {topMissing.length > 0 && (
        <div className='flex flex-col gap-1'>
          <span className='text-xs font-medium uppercase tracking-wide text-gray-500'>
            Answers that would sharpen this most
          </span>
          <ul className='flex flex-col gap-0.5'>
            {topMissing.map((field) => (
              <li
                key={field.id}
                className='flex items-baseline gap-2 text-sm text-gray-700'
              >
                <span>{field.label}</span>
                <span className='text-xs text-gray-400'>
                  +{field.points} pts
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {developerMode && (
        <div className='mt-1 flex flex-col gap-2 border-t border-dashed border-gray-200 pt-2'>
          <div className='flex gap-4 text-sm text-gray-700'>
            <span>
              DCS (have): <span className='font-semibold'>{score.dcs}</span>
            </span>
            <span>
              PCS (want): <span className='font-semibold'>{score.pcs}</span>
            </span>
            <span>
              Hard-gated:{' '}
              <span className='font-semibold'>
                {score.hardGated ? 'yes' : 'no'}
              </span>
            </span>
          </div>
          <table className='w-full text-left text-xs'>
            <thead>
              <tr className='text-gray-500'>
                <th className='py-0.5 pr-2 font-medium'>Field</th>
                <th className='py-0.5 pr-2 font-medium'>Side</th>
                <th className='py-0.5 pr-2 font-medium'>Status</th>
                <th className='py-0.5 pr-2 font-medium'>Points</th>
                <th className='py-0.5 font-medium'>Known</th>
              </tr>
            </thead>
            <tbody>
              {score.fields.map((field) => (
                <tr key={field.id} className='border-t border-gray-100'>
                  <td className='py-0.5 pr-2 text-gray-700'>{field.label}</td>
                  <td className='py-0.5 pr-2 text-gray-500'>{field.side}</td>
                  <td className='py-0.5 pr-2 text-gray-500'>{field.status}</td>
                  <td className='py-0.5 pr-2 text-gray-500'>{field.points}</td>
                  <td className='py-0.5'>
                    {field.earned ? (
                      <span className='text-green-600'>yes</span>
                    ) : (
                      <span className='text-red-500'>no</span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
